import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import AlertPopup from "./components/AlertPopup";
import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";
import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowUpward,
  ArrowDownward,
  ArrowDropDown,
  ArrowDropUp,
} from "@mui/icons-material";

import dayjs from "dayjs";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";
import placeholderImage from "./assets/r3play-logo-sml.png";

import { 
  VIDEOS_BY_USER_UUID,
  UPDATE_VIDEO_STATUS,
  DELETE_VIDEO 
} from "./graphQL/video_qry";

// Status Options
const STATUS_OPTIONS = ["All", "Published", "Draft"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const MyVideos = () => {
  const navigate = useNavigate();
  const isTablet = useIsTablet(1360);
  const isMobile = useIsMobile(833);
  const [statusFilter, setStatusFilter] = useState("All");
  const [mobileSortBy, setMobileSortBy] = useState("publishedOn");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("publishedOn");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [videos, setVideos] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  const [getVideosData] = useManualQuery(VIDEOS_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  const [updateVideoStatus] = useMutation(UPDATE_VIDEO_STATUS, {
    awaitRefetchQueries: true,
  });

  const [deleteVideo] = useMutation(DELETE_VIDEO, {
    awaitRefetchQueries: true,
  });

  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    videoUuid: null,
  });

  // Helper function to get video thumbnail from any platform
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);

  // Initialize videos data
  const setVideoList = async () => {    
    if (!userUuid) return;
    const {
      loading: loadingVideoListData,
      error: errorVideoListData,
      data: videoListData,
    } = await getVideosData({
      variables: {
        userUuid: userUuid,
      },
    });
    if (!loadingVideoListData && 
        videoListData && 
        videoListData.videosByUseruuid
        ) {              
      setVideos(
        videoListData.videosByUseruuid.map(video => ({
          id: video.videoId,
          uuid: video.uuid,
          title: video.title,          
          content: video.content,
          videoUrl: video.videoUrl,
          createdOn: dayjs(video.createDate).format("MMM D, YYYY"),        
          publishedOn: video.isPublished ? dayjs(video.publishedDate).format("MMM D, YYYY") : "",
          status: video.isPublished ? "Published" : "Draft",
        }))
      );
      setIsLoaded(true);           
    } else {
      console.log("errorCurrVideoData: ", errorVideoListData);           
    }
  };

  useEffect(() => {
    (async () => setVideoList())();
  }, [userUuid]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Filter change handlers
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleMobileSortChange = (event) => {
    const sortValue = event.target.value;
    setMobileSortBy(sortValue);
    handleRequestSort(sortValue);
    setPage(0);
  };

  // Popup handlers
  const handlePublishClick = (videoUuid) => {
    setAlertPopup({
      open: true,
      type: "publish",
      videoUuid: videoUuid,
    });
  };

  const handleUnpublishClick = (videoUuid) => {
    setAlertPopup({
      open: true,
      type: "unpublish",
      videoUuid: videoUuid,
    });
  };

  const handleDeleteClick = (videoUuid) => {
    setAlertPopup({
      open: true,
      type: "delete",
      videoUuid: videoUuid,
    });
  };

  const handleConfirmAction = async() => {
    if (alertPopup.type === "delete"){
      const {
        loading: loadingVideoDelete,
        error: errorVideoDelete,
        data: videoDelete,
      } = await deleteVideo({
        variables: {
          videoUuid: alertPopup.videoUuid,                   
        },
      });
      if (!loadingVideoDelete && 
          videoDelete && 
          videoDelete.deleteVideo
          ){ 
        setVideos((prevVideos) =>
        prevVideos.filter((video) => video.uuid !== alertPopup.videoUuid)
      );
      } else {
        console.log("errorVideoDelete: ", errorVideoDelete);
      };
    } else {
      let isPublished = false
      if (alertPopup.type === "publish") {
        isPublished = true      
      } else if (alertPopup.type === "unpublish") {
        isPublished = false
      };
      const {
        loading: loadingVideoStatus,
        error: errorVideoStatus,
        data: videoStatus,
      } = await updateVideoStatus({
        variables: {
          videoUuid: alertPopup.videoUuid,
          isPublished: isPublished 
        },
      });
      if (!loadingVideoStatus && 
          videoStatus && 
          videoStatus.updateVideoStatus
      ){ 
        setVideos((prevVideos) =>
          prevVideos.map(video =>
            video.uuid === alertPopup.videoUuid
            ? {
                ...video,
                publishedOn: isPublished ? (new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })) : "",
                status: isPublished ? "Published": "Draft",
              }
            : video
          )
        );
      } else {
        console.log("errorVideoData: ", errorVideoStatus);
      };
    };
    setAlertPopup({ open: false, type: "", videoUuid: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", videoUuid: null });
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Filter and sort data
  const getFilteredData = () => {
    let filteredData = videos;

    if (statusFilter !== "All") {
      filteredData = filteredData.filter(
        (video) => video.status === statusFilter
      );
    }

    if (orderBy) {
      filteredData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "createdOn" || orderBy === "publishedOn") {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }

        if (orderBy === "status") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  };

  const filteredData = getFilteredData();
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet>
        <title>R3PLAY - My Videos</title>
        <meta name="description" content="Manage and edit your videos" />
      </Helmet>

      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "16px",
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#333",
              mb: 1,
            }}
          >
            My Videos
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#666",
              mb: 2,
            }}
          >
            Manage and edit your videos
          </Typography>

          {/* Mobile Filter Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
            }}
          >
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{
                width: "50%",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              SelectProps={{
                IconComponent: ArrowDropDown,
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) 
        : isLoaded && videos.length === 0 ? 
        (
          <EmptyState 
            type="videos"
            title="No Videos Available"
            description="No videos are available right now. Please check back later."
          />
        ) : (
          <>
            {/* Mobile Sort Panel */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
                width: "100%",
              }}
            >
              <Box sx={{ width: "230px", position: "relative" }} data-sort-panel>
                <TextField
                  label="Sort by"
                  value={
                    mobileSortBy
                      ? (() => {
                          const options = {
                            title: "Title",
                            createdOn: "Created on",
                            publishedOn: "Published on",
                            status: "Status",
                          };
                          return options[mobileSortBy] || "";
                        })()
                      : ""
                  }
                  onClick={() => setSortPanelOpen(!sortPanelOpen)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {mobileSortBy &&
                          (order === "desc" ? (
                            <ArrowDownward sx={{ fontSize: 20, color: "#666" }} />
                          ) : (
                            <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                          ))}
                        {sortPanelOpen ? (
                          <ArrowDropUp sx={{ color: "#666" }} />
                        ) : (
                          <ArrowDropDown sx={{ color: "#666" }} />
                        )}
                      </Box>
                    ),
                  }}
                  sx={{
                    width: "230px",
                    cursor: "pointer",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      paddingRight: "8px",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      fontSize: "14px",
                      "&.Mui-focused": {
                        color: "#666",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      pr: 0,
                    },
                    "& .MuiOutlinedInput-input": {
                      cursor: "pointer",
                    },
                  }}
                />

                {/* Sort options panel */}
                {sortPanelOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "40px",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                    }}
                  >
                    <RadioGroup
                      value={mobileSortBy}
                      onChange={(e) => {
                        setMobileSortBy(e.target.value);
                        setOrder("desc");
                        setOrderBy(e.target.value);
                        setPage(0);
                      }}
                    >
                      {[
                        { value: "title", label: "Title" },
                        { value: "createdOn", label: "Created on" },
                        { value: "publishedOn", label: "Published on" },
                      ].map((option) => (
                        <Box
                          key={option.value}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                            pl: 0,
                            py: 0.5,
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <FormControlLabel
                            value={option.value}
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  color: "#666",
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: "14px", color: "#333" }}>
                                {option.label}
                              </Typography>
                            }
                            sx={{ margin: 0, flex: 1 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: "55px",
                              justifyContent: "space-between",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMobileSortBy(option.value);
                                setOrder("desc");
                                setOrderBy(option.value);
                                setPage(0);
                                setSortPanelOpen(false);
                              }}
                              sx={{
                                p: 0.25,
                                backgroundColor:
                                  mobileSortBy === option.value && order === "desc"
                                    ? "#fce4ec"
                                    : "transparent",
                              }}
                            >
                              <ArrowDownward
                                fontSize="small"
                                sx={{ fontSize: 20, color: "#666" }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMobileSortBy(option.value);
                                setOrder("asc");
                                setOrderBy(option.value);
                                setPage(0);
                                setSortPanelOpen(false);
                              }}
                              sx={{
                                p: 0.25,
                                backgroundColor:
                                  mobileSortBy === option.value && order === "asc"
                                    ? "#fce4ec"
                                    : "transparent",
                              }}
                            >
                              <ArrowUpward
                                fontSize="small"
                                sx={{ fontSize: 20, color: "#666" }}
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </RadioGroup>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Mobile Cards */}
            {paginatedData.map((video, index) => (
              <Box
                key={video.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#FDECF0" : "white",
                  borderRadius: "8px",
                  mb: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                <Table>
                  <TableBody>
                    {/* Row 1: Video + Title */}
                    <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                      <TableCell sx={{ py: 2, width: "125px", border: "none" }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            backgroundColor: "white",
                            borderRadius: "8px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="img"
                            src={
                              getVideoThumbnail(video.videoUrl) || placeholderImage
                            }
                            alt={video.title}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImage;
                            }}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",//"contain" so the logo displays properly without being stretched
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2, border: "none" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "500",
                            lineHeight: 1.4,
                          }}
                        >
                          {video.title}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Row 2: Created on + Published on */}
                    <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                      <TableCell
                        sx={{
                          py: 2,
                          border: "none",
                          verticalAlign: "top",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Created on
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {video.createdOn}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 2,
                          border: "none",
                          verticalAlign: "top",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Published on
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {video.publishedOn || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Row 3: Status and Actions */}
                    <TableRow>
                      <TableCell
                        sx={{
                          pt: 2,
                          pb: 0,
                          border: "none",
                          verticalAlign: "top",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: "bold",
                            mb: 0.5,
                          }}
                        >
                          Status
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "white",
                            color: "#333",
                            border: "1px solid #000",
                            fontSize: "12px",
                            fontWeight: "500",
                            width: "100px",
                            height: "30px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          {video.status}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          pt: 2,
                          pb: 1,
                          border: "none",
                          verticalAlign: "middle",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-start",
                          }}
                        >
                          {video.publishedOn === "" ? (
                            <StyledTooltip title="Publish this video">
                              <IconButton
                                size="small"
                                onClick={() => handlePublishClick(video.uuid)}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <AssignmentTurnedInOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          ) : (
                            <StyledTooltip title="Unpublish this video">
                              <IconButton
                                size="small"
                                onClick={() => handleUnpublishClick(video.uuid)}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <FactCheckOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          )}
                          <StyledTooltip title="View this video">
                            <IconButton
                              size="small"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <Link
                                component={RouterLink}
                                to={`/video/${video.uuid}`}
                                target="_blank"
                                color="black"
                                underline="none"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </Link>
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="Edit this video">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`/video/${video.uuid}/edit`, {
                                  state: { video }
                                })
                              }
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                          <StyledTooltip title="Delete this video">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(video.uuid)}
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <DeleteOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Card bottom padding */}
                <Box sx={{ pb: 2 }} />
              </Box>
            ))}

            {/* Mobile Pagination */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 15, 20]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 1,
                    paddingRight: 1,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#666",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "14px",
                  },
                  "& .MuiTablePagination-actions": {
                    "& .MuiIconButton-root": {
                      padding: "6px",
                    },
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "91px",
            p: 1,
            pl: 2,
            pr: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Title Section */}
            <Box>
              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                My Videos
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                Manage and edit your videos
              </Typography>
            </Box>

            {/* Filter Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{
                  width: "150px",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
                SelectProps={{
                  IconComponent: ArrowDropDown,
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        {!isLoaded ? (
          <Box p={4} textAlign="center">Loading...</Box>
        ) 
        : isLoaded && videos.length === 0 ? 
        (
          <EmptyState 
            type="videos"
            title="No Videos Available"
            description="No videos are available right now. Please check back later."
          />
        ) : (
          <>
            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "white" }}>
                    <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                      Image
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                      <TableSortLabel
                        active={orderBy === "title"}
                        direction={orderBy === "title" ? order : "asc"}
                        onClick={() => handleRequestSort("title")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                      <TableSortLabel
                        active={orderBy === "createdOn"}
                        direction={orderBy === "createdOn" ? order : "asc"}
                        onClick={() => handleRequestSort("createdOn")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Created on
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                      <TableSortLabel
                        active={orderBy === "publishedOn"}
                        direction={orderBy === "publishedOn" ? order : "asc"}
                        onClick={() => handleRequestSort("publishedOn")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Published on
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleRequestSort("status")}
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          "&.Mui-active": {
                            color: "#333",
                          },
                          "&:hover": {
                            color: "#333",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#333 !important",
                          },
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        py: 2,
                        minWidth: "150px",
                        textAlign: "center",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((video) => (
                    <React.Fragment key={video.id}>
                      <TableRow
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f9f9f9",
                          },
                          ...(isTablet && {
                            "& td": {
                              borderBottom: "none",
                            },
                          }),
                        }}
                      >
                        <TableCell sx={{ py: 2, pb: isTablet ? 0 : 2 }}>
                          <Box
                            sx={{
                              width: isTablet ? 40 : 80,
                              height: isTablet ? 40 : 80,
                              backgroundColor: "white",
                              borderRadius: "8px",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              component="img"
                              src={
                                getVideoThumbnail(video.videoUrl) ||
                                placeholderImage
                              }
                              alt={video.title}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = placeholderImage;
                              }}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",// or "contain" so the logo displays properly without being stretched
                              }}
                            />
                          </Box>
                        </TableCell>

                        {/* Title Cell - Spans 6 columns on tablet, normal on desktop */}
                        <TableCell
                          colSpan={isTablet ? 6 : 1}
                          sx={{
                            py: isTablet ? 1 : 2,
                            maxWidth: "300px",
                            "@media (max-width: 1360px)": {
                              maxWidth: "none",
                              paddingBottom: 0,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              fontWeight: "500",
                              lineHeight: 1.4,
                            }}
                          >
                            {video.title}
                          </Typography>
                        </TableCell>

                        {/* Desktop columns - hidden on tablet */}
                        {!isTablet && (
                          <>
                            <TableCell sx={{ py: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                }}
                              >
                                {video.createdOn}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#333",
                                }}
                              >
                                {video.publishedOn || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Box
                                sx={{
                                  backgroundColor: "white",
                                  color: "#333",
                                  border: "1px solid #000",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  width: "100px",
                                  height: "30px",
                                  minWidth: "80px",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {video.status}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  justifyContent: "center",
                                }}
                              >
                                {video.publishedOn === "" ? (
                                  <StyledTooltip title="Publish this video">
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePublishClick(video.uuid)}
                                      sx={{
                                        color: "#666",
                                        "&:hover": {
                                          backgroundColor: "rgba(0,0,0,0.04)",
                                        },
                                      }}
                                    >
                                      <AssignmentTurnedInOutlined fontSize="small" />
                                    </IconButton>
                                  </StyledTooltip>
                                ) : (
                                  <StyledTooltip title="Unpublish this video">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUnpublishClick(video.uuid)}
                                      sx={{
                                        color: "#666",
                                        "&:hover": {
                                          backgroundColor: "rgba(0,0,0,0.04)",
                                        },
                                      }}
                                    >
                                      <FactCheckOutlined fontSize="small" />
                                    </IconButton>
                                  </StyledTooltip>
                                )}
                                <StyledTooltip title="View this video">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <Link
                                      component={RouterLink}
                                      to={`/video/${video.uuid}`}
                                      target="_blank"
                                      color="black"
                                      underline="none"
                                      sx={{
                                        color: "#666",
                                        "&:hover": {
                                          backgroundColor: "rgba(0,0,0,0.04)",
                                        },
                                      }}
                                    >
                                      <VisibilityOutlined fontSize="small" />
                                    </Link>
                                  </IconButton>
                                </StyledTooltip>
                                <StyledTooltip title="Edit this video">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigate(`/video/${video.uuid}/edit`, {
                                        state: { video }
                                      })
                                    }
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <EditOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                                <StyledTooltip title="Delete this video">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(video.uuid)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <DeleteOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>

                      {/* Second row for tablet view */}
                      {isTablet && (
                        <TableRow
                          key={`${video.id}-details`}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f9f9f9",
                            },
                            "& td": {
                              borderTop: "none",
                            },
                          }}
                        >
                          <TableCell sx={{ py: 0 }}></TableCell>
                          <TableCell sx={{ py: 0 }}></TableCell>

                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {video.createdOn}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {video.publishedOn || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 2 }}>
                            <Box
                              sx={{
                                backgroundColor: "white",
                                color: "#333",
                                border: "1px solid #000",
                                fontSize: "12px",
                                fontWeight: "500",
                                width: "100px",
                                height: "30px",
                                minWidth: "80px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {video.status}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ pt: 0, pb: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              {video.publishedOn === "" ? (
                                <StyledTooltip title="Publish this video">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePublishClick(video.uuid)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <AssignmentTurnedInOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                              ) : (
                                <StyledTooltip title="Unpublish this video">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUnpublishClick(video.uuid)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <FactCheckOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                              )}
                              <StyledTooltip title="View this video">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <Link
                                    component={RouterLink}
                                    to={`/video/${video.uuid}`}
                                    target="_blank"
                                    color="black"
                                    underline="none"
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <VisibilityOutlined fontSize="small" />
                                  </Link>
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="Edit this video">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    navigate(`/video/${video.uuid}/edit`, {
                                      state: { video }
                                    })
                                  }
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="Delete this video">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(video.uuid)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <DeleteOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 15, 20]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "14px",
                    color: "#666",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "14px",
                  },
                }}
              />
            </TableContainer>
          </>
        )}
      </Box>
      <FloatingMenu left={floatMenuLeftMargin} />

      {/* Alert Popup */}
      <AlertPopup
        open={alertPopup.open}
        onClose={handleCancelAction}
        title={
          alertPopup.type === "publish"
            ? "Draft to Publish"
            : alertPopup.type === "unpublish"
            ? "Publish to Draft"
            : "Delete Video"
        }
        message={
          alertPopup.type === "publish"
            ? "Are you ready to publish this video?"
            : alertPopup.type === "unpublish"
            ? "Are you sure you want to unpublish this video?"
            : "Are you sure you want to delete this video?"
        }
        confirmText={
          alertPopup.type === "publish"
            ? "YES, PUBLISH"
            : alertPopup.type === "unpublish"
            ? "YES, UNPUBLISH"
            : "YES, DELETE"
        }
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

export default MyVideos;
